import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { ACCENT_COLORS, SCREENS, isNewYearPeriod } from './utils/constants';
import SplashScreen from './components/SplashScreen';
import MatrixRain from './components/MatrixRain';
import Snowfall from './components/Snowfall';
import TabButton from './components/TabButton';
import ScheduleScreen from './components/ScheduleScreen';
import UnifiedWindowScreen from './components/UnifiedWindowScreen';
import SettingsScreen from './components/SettingsScreen';
import ErrorScreen from './components/ErrorScreen';
import './App.css';

/* ---- Lazy placeholders ---- */
const FreshmanScreen = React.lazy(() => import('./components/FreshmanScreen').catch(() => ({ default: () => <Placeholder icon="school-outline" text="Первокурснику" /> })));
const NewsScreen = React.lazy(() => import('./components/NewsScreen').catch(() => ({ default: () => <Placeholder icon="newspaper-outline" text="Новости" /> })));

const Placeholder = ({ icon, text }) => (
  <div className="empty-state">
    <ion-icon name={icon} style={{ fontSize: 48, color: 'var(--text-tertiary)' }}></ion-icon>
    <p className="empty-state__text">{text}</p>
    <p className="empty-state__subtext">Раздел в разработке</p>
  </div>
);

const TABS = [
  { id: 'schedule', label: SCREENS.SCHEDULE, icon: 'calendar-outline', activeIcon: 'calendar' },
  { id: 'unified', label: SCREENS.UNIFIED_WINDOW, icon: 'chatbubbles-outline', activeIcon: 'chatbubbles' },
  { id: 'freshman', label: SCREENS.FRESHMAN, icon: 'school-outline', activeIcon: 'school' },
  { id: 'news', label: SCREENS.NEWS, icon: 'newspaper-outline', activeIcon: 'newspaper' },
  { id: 'settings', label: SCREENS.SETTINGS, icon: 'settings-outline', activeIcon: 'settings' },
];

const SETTINGS_SUB_TITLES = { appearance: 'Внешний вид', about: 'О приложении', developer: 'Меню разработчика' };
const FRESHMAN_SUB_TITLES = { buildings: 'Корпуса ХГУ', groups: 'Полезные группы', bells: 'Расписание звонков', glossary: 'Словарь аббревиатур' };

function App() {
  const navigate = useNavigate();
  const location = useLocation();

  const [showSplash, setShowSplash] = useState(true);
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');
  const [accentColor, setAccentColor] = useState(() => localStorage.getItem('accentColor') || 'green');
  const [isNewYearMode, setIsNewYearMode] = useState(() => {
    const saved = localStorage.getItem('new_year_mode');
    return saved !== null ? JSON.parse(saved) : isNewYearPeriod();
  });
  const [screenKey, setScreenKey] = useState(0);
  const tabBarRef = useRef(null);
  const tabRefs = useRef({});
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });

  // Derive activeTab and subScreen from URL
  const pathParts = location.pathname.split('/').filter(Boolean);
  const isErrorPage = pathParts[0] === 'error' || (!['schedule', 'unified', 'freshman', 'news', 'settings', ''].includes(pathParts[0]) && pathParts[0] !== undefined);
  const activeTab = ['schedule', 'unified', 'freshman', 'news', 'settings'].includes(pathParts[0]) ? pathParts[0] : 'schedule';
  const subScreen = pathParts[1] || null;

  useEffect(() => { const t = setTimeout(() => setShowSplash(false), 1800); return () => clearTimeout(t); }, []);

  /* Update floating tab indicator position */
  useEffect(() => {
    const updateIndicator = () => {
      const bar = tabBarRef.current;
      const btn = tabRefs.current[activeTab];
      if (bar && btn) {
        const barRect = bar.getBoundingClientRect();
        const btnRect = btn.getBoundingClientRect();
        setIndicatorStyle({
          left: btnRect.left - barRect.left + btnRect.width * 0.1,
          width: btnRect.width * 0.8,
        });
      }
    };
    // Small delay to ensure layout is ready
    const t = setTimeout(updateIndicator, 50);
    window.addEventListener('resize', updateIndicator);
    return () => { clearTimeout(t); window.removeEventListener('resize', updateIndicator); };
  }, [activeTab, showSplash]);

  /* Theme application */
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
    if (theme === 'matrix') {
      setAccentColor('matrix');
      localStorage.setItem('accentColor', 'matrix');
    }
  }, [theme]);

  useEffect(() => {
    const ac = theme === 'matrix' ? 'matrix' : accentColor;
    document.documentElement.setAttribute('data-accent', ac);
    const colors = ACCENT_COLORS[ac] || ACCENT_COLORS.green;
    document.documentElement.style.setProperty('--accent', colors.primary);
    document.documentElement.style.setProperty('--accent-light', colors.light);
    document.documentElement.style.setProperty('--accent-dark', colors.dark);
    document.documentElement.style.setProperty('--accent-glass', colors.glass);
    document.documentElement.style.setProperty('--accent-glass-border', colors.glassBorder);
    localStorage.setItem('accentColor', ac);
  }, [accentColor, theme]);

  const handleNewYearModeChange = useCallback((value) => {
    setIsNewYearMode(value);
    localStorage.setItem('new_year_mode', JSON.stringify(value));
  }, []);

  const handleTabPress = (tabId) => {
    const tabPath = `/${tabId}`;
    if (activeTab === tabId) {
      if (subScreen) {
        navigate(tabPath);
        return;
      }
      setScreenKey(k => k + 1);
      return;
    }
    navigate(tabPath);
  };

  const headerTitle = activeTab === 'settings' && subScreen
    ? SETTINGS_SUB_TITLES[subScreen] || TABS.find(t => t.id === activeTab)?.label
    : activeTab === 'freshman' && subScreen
      ? FRESHMAN_SUB_TITLES[subScreen] || TABS.find(t => t.id === activeTab)?.label
      : TABS.find(t => t.id === activeTab)?.label || '';

  const showBackButton = (activeTab === 'settings' || activeTab === 'freshman') && !!subScreen;

  const handleBackPress = () => {
    navigate(`/${activeTab}`);
  };

  if (showSplash) return <SplashScreen theme={theme} />;

  return (
    <div className="app">
      <Helmet>
        <title>Мой ИТИ ХГУ</title>
        <meta name="theme-color" content={theme === 'dark' ? '#000000' : theme === 'matrix' ? '#0D0208' : '#f2f2f7'} />
      </Helmet>

      {theme === 'matrix' && <MatrixRain />}
      {isNewYearMode && <Snowfall theme={theme} />}

      {/* Header */}
      {!isErrorPage && (
        <header className="glass-header">
          {showBackButton && (
            <button className="glass-header__back" onClick={handleBackPress}>
              <ion-icon name="chevron-back" style={{ fontSize: 20 }}></ion-icon>
              Назад
            </button>
          )}
          <span className="glass-header__title">{headerTitle}</span>
        </header>
      )}

      {/* Content */}
      <main className="main-content">
        <React.Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><div className="spinner" /></div>}>
          <div className="screen-transition" key={activeTab + '-' + screenKey}>
            <Routes>
              <Route path="/" element={<ScheduleScreen theme={theme} accentColor={accentColor} isNewYearMode={isNewYearMode} />} />
              <Route path="/schedule" element={<ScheduleScreen theme={theme} accentColor={accentColor} isNewYearMode={isNewYearMode} />} />
              <Route path="/unified" element={<UnifiedWindowScreen />} />
              <Route path="/freshman/*" element={<FreshmanScreen theme={theme} />} />
              <Route path="/news" element={<NewsScreen theme={theme} />} />
              <Route path="/settings/*" element={
                <SettingsScreen theme={theme} accentColor={accentColor}
                  setTheme={setTheme} setAccentColor={setAccentColor}
                  isNewYearMode={isNewYearMode} onNewYearModeChange={handleNewYearModeChange} />
              } />
              <Route path="/error" element={<ErrorScreen />} />
              <Route path="*" element={<ErrorScreen code="404" />} />
            </Routes>
          </div>
        </React.Suspense>
      </main>

      {/* Tab Bar */}
      {!isErrorPage && (
        <nav className="glass-tab-bar">
          <div className="glass-tab-bar__inner" ref={tabBarRef}>
            <div className="glass-tab-bar__indicator"
              style={{ left: indicatorStyle.left, width: indicatorStyle.width }} />
            {TABS.map(tab => (
              <TabButton key={tab.id}
                ref={el => { tabRefs.current[tab.id] = el; }}
                icon={activeTab === tab.id ? tab.activeIcon : tab.icon}
                label={tab.label}
                isActive={activeTab === tab.id}
                onPress={() => handleTabPress(tab.id)} />
            ))}
          </div>
        </nav>
      )}
    </div>
  );
}

export default App;
